import React, { useState, useRef, useEffect } from "react";
import { Chips, ChipsChangeEvent } from "primereact/chips";
import { Link } from "react-router-dom";
import "../../../../style/css/email.css";
import api from "../../../../services/api";
import { showBasicAlert } from "../../../../core/common/useAlert";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export interface Email {
  uid: number;
  from: string;
  to: string;
  cc: string;
  subject: string;
  text: string;
  date: string;
  messageId: string;
  inReplyTo: string | null;
  references: string[];
  isReply: boolean;
  attachments: any[];
  profileImage?: string;
  isRead?: boolean;
}

interface EmailData {
  to: string[];
  cc?: string[];
  subject: string;
  message: string;
  inReplyTo?: string;
  references?: string[];
  replyAll?: boolean;
  attachments?: File[];
}
interface Attachment {
  filename: string;
  size: number;
  contentType: string;
  content: string; // base64 encoded content
}
interface ComposeEmailProps {
  onSubmit: (data: FormData | EmailData) => void;
  onCancel: () => void;
  isLoading: boolean;
  replyToEmail?: {
    from: string;
    to: string;
    subject: string;
    messageId: string;
    references: string[];
    isReply: boolean;
  };
  draftEmail?:
    | (Email & {
        attachments?: Attachment[];
      })
    | null;
}
const quillModules = {
  toolbar: [
    [{ font: [] }],
    ["bold", "italic", "underline"],
    [{ color: [] }, { background: [] }],
    [{ script: "super" }, { script: "sub" }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ align: [] }],
    ["code-block"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};

const quillFormats = [
  "font",
  "bold",
  "italic",
  "underline",
  "color",
  "background",
  "script",
  "header",
  "align",
  "code-block",
  "list",
  "bullet",
  "link",
];

const ComposeEmail: React.FC<ComposeEmailProps> = ({
  onSubmit,
  onCancel,
  isLoading,
  replyToEmail,
  draftEmail,
}) => {
  // Filter out "Unknown Recipient" and "Unknown cc" from initial values
  const initialTo = draftEmail?.to
    ? draftEmail.to
        .split(",")
        .filter((item) => item.trim() !== "Unknown Recipient")
    : replyToEmail
    ? [replyToEmail.from]
    : [];

  const initialCc = draftEmail?.cc
    ? draftEmail.cc.split(",").filter((item) => item.trim() !== "Unknown cc")
    : [];

  const [to, setTo] = useState<string[]>(initialTo);
  const [cc, setCc] = useState<string[]>(initialCc);
  const [subject, setSubject] = useState(
    draftEmail?.subject || (replyToEmail ? `Re: ${replyToEmail.subject}` : "")
  );
  const [message, setMessage] = useState(draftEmail?.text || "");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showCc, setShowCc] = useState<boolean>(initialCc.length > 0);
  const [replyAll, setReplyAll] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );

  const MAX_ATTACHMENTS = parseInt(
    process.env.REACT_APP_EMAIL_MAX_ATTACHMENTS!,
    10
  );
  const MAX_FILE_SIZE =
    parseInt(process.env.REACT_APP_EMAIL_MAX_FILE_SIZE_MB!, 10) * 1024 * 1024;

  const signature = localStorage.getItem("signature");

  useEffect(() => {
    if (draftEmail) {
      const filteredTo = draftEmail.to
        ? draftEmail.to
            .split(",")
            .filter((item) => item.trim() !== "Unknown Recipient")
        : [];
      const filteredCc = draftEmail.cc
        ? draftEmail.cc
            .split(",")
            .filter((item) => item.trim() !== "Unknown cc")
        : [];

      setTo(filteredTo);
      setCc(filteredCc);
      setSubject(draftEmail.subject || "");
      setMessage(draftEmail.text || "");
      setShowCc(filteredCc.length > 0);

      // Set existing attachments if they exist
      if (draftEmail.attachments && draftEmail.attachments.length > 0) {
        setExistingAttachments(draftEmail.attachments);
      }
    }
  }, [draftEmail]);

  const base64ToFile = (
    base64: string,
    filename: string,
    contentType: string
  ): File => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });
    return new File([blob], filename, { type: contentType });
  };

  const removeExistingAttachment = (index: number) => {
    setExistingAttachments(existingAttachments.filter((_, i) => i !== index));
  };

  const validateEmail = (email: string): boolean => {
    if (!email || email === "Unknown Recipient" || email === "Unknown cc")
      return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const customChip = (item: string) => {
    const isValid = validateEmail(item);
    return (
      <div
        className={`p-chip p-component ${
          isValid ? "p-chip-valid" : "p-chip-invalid"
        }`}
        style={{
          background: isValid ? "#e8f0fe" : "#fce8e6",
          color: isValid ? "#1a73e8" : "#d93025",
          borderRadius: "16px",
          padding: "2px 8px",
          margin: "2px",
          display: "inline-flex",
          alignItems: "center",
          fontSize: "14px",
        }}
      >
        <span>{item}</span>
      </div>
    );
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentValues: string[],
    setValues: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (e.key === " ") {
      // Handle space to create chip
      const inputElement = e.target as HTMLInputElement;
      const value = inputElement.value.trim();
      if (value && value !== "Unknown Recipient" && value !== "Unknown cc") {
        setValues([...currentValues, value]);
        inputElement.value = "";
        e.preventDefault();
      }
    } else if (e.key === "Enter") {
      // Prevent form submission but still allow adding chips
      e.preventDefault();
      const inputElement = e.target as HTMLInputElement;
      const value = inputElement.value.trim();
      if (value && value !== "Unknown Recipient" && value !== "Unknown cc") {
        setValues([...currentValues, value]);
        inputElement.value = "";
      }
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    currentValues: string[],
    setValues: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const value = e.target.value.trim();
    if (value && value !== "Unknown Recipient" && value !== "Unknown cc") {
      setValues([...currentValues, value]);
      e.target.value = "";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const invalidEmails = [
      ...to.filter((email) => !validateEmail(email)),
      ...cc.filter((email) => !validateEmail(email)),
    ];

    if (invalidEmails.length > 0) {
      showBasicAlert(
        "Please correct invalid email addresses: " + invalidEmails.join(", ")
      );
      return;
    }

    // Convert remaining existing attachments to File objects
    const allAttachments = [...attachments];
    existingAttachments.forEach((attachment) => {
      const file = base64ToFile(
        attachment.content,
        attachment.filename,
        attachment.contentType
      );
      allAttachments.push(file);
    });

    if (replyToEmail) {
      const replyData: EmailData = {
        to: to,
        cc: cc.length > 0 ? cc : undefined,
        subject,
        message,
        inReplyTo: replyToEmail.messageId,
        references: [...replyToEmail.references, replyToEmail.messageId],
        replyAll: replyAll,
        attachments: allAttachments,
      };
      onSubmit(replyData);
    } else {
      const formData = new FormData();
      formData.append("to", JSON.stringify(to));
      formData.append("cc", JSON.stringify(cc));
      formData.append("subject", subject);
      formData.append("message", message);
      allAttachments.forEach((file) => {
        formData.append("attachments", file);
      });
      onSubmit(formData);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);

      if (attachments.length + newFiles.length > MAX_ATTACHMENTS) {
        showBasicAlert(`You can attach maximum ${MAX_ATTACHMENTS} files`);
        return;
      }

      for (const file of newFiles) {
        if (file.size > MAX_FILE_SIZE) {
          showBasicAlert(
            `File ${file.name} is too large (max ${
              MAX_FILE_SIZE / 1024 / 1024
            }MB)`
          );
          return;
        }
      }

      setAttachments((prevAttachments) => [...prevAttachments, ...newFiles]);
      e.target.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const saveDraft = async () => {
    setIsSavingDraft(true);
    try {
      const formData = new FormData();
      formData.append("to", JSON.stringify(to));
      formData.append("cc", JSON.stringify(cc));
      formData.append("subject", subject);
      formData.append("message", message);

      if (replyToEmail) {
        formData.append("inReplyTo", replyToEmail.messageId);
        formData.append(
          "references",
          JSON.stringify([...replyToEmail.references, replyToEmail.messageId])
        );
        formData.append("replyAll", replyAll.toString());
      }

      // Add new attachments
      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      // Add remaining existing attachments
      existingAttachments.forEach((attachment) => {
        formData.append("existingAttachments", JSON.stringify(attachment));
      });

      // If this is an existing draft, include its UID
      if (draftEmail?.uid) {
        formData.append("draftUid", draftEmail.uid.toString());
      }

      formData.append("signature", signature ?? "");

      const response = await api.post("mailService/saveDraftMail", formData);

      setShowCloseDialog(false);
      onCancel();
    } catch (error) {
      console.error("Failed to save draft:", error);
      showBasicAlert("Failed to save draft. Please try again.");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleCloseClick = () => {
    if (to.length > 0 || subject || message || attachments.length > 0) {
      setShowCloseDialog(true);
    } else {
      onCancel();
    }
  };

  const handleDiscard = () => {
    setShowCloseDialog(false);
    onCancel();
  };

  return (
    <div className="compose-email-container">
      <div className="compose-header d-flex align-items-center justify-content-between bg-dark p-3">
        <h5 className="text-white">
          {replyToEmail
            ? "Reply Email"
            : draftEmail
            ? "Edit Draft"
            : "New Email"}
        </h5>
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn-close custom-btn-close bg-transparent fs-16 text-white position-static"
            onClick={handleCloseClick}
            disabled={isLoading || isSavingDraft}
          >
            <i className="ti ti-x" />
          </button>
        </div>
      </div>

      <div
        className={`modal ${showCloseDialog ? "show d-block" : ""}`}
        style={{ zIndex: 1100 }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Save Draft?</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowCloseDialog(false)}
                disabled={isLoading || isSavingDraft}
              ></button>
            </div>
            <div className="modal-body">
              <p>Do you want to save this email as draft before closing?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={handleDiscard}
                disabled={isLoading || isSavingDraft}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={saveDraft}
                disabled={isLoading || isSavingDraft}
              >
                {isSavingDraft ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Saving...
                  </>
                ) : (
                  "Save Draft"
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowCloseDialog(false)}
                disabled={isLoading || isSavingDraft}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      {showCloseDialog && (
        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 1050 }}
        ></div>
      )}
      <div className="compose-content">
        <form
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
              e.preventDefault();
            }
          }}
        >
          <div className="position-relative border-bottom chip-with-image ">
            <div className="tag-with-img d-flex align-items-center">
              <label className="form-label mb-0">To</label>
              <Chips
                value={to}
                className="input-tags form-control border-0 h-100 w-100"
                onChange={(e: ChipsChangeEvent) => setTo(e.value || [])}
                onKeyDown={(e) => handleKeyDown(e, to, setTo)}
                onBlur={(e) => handleBlur(e, to, setTo)}
                itemTemplate={customChip}
                placeholder="Enter email addresses"
              />
            </div>
            <div className="d-flex align-items-center email-cc">
              <Link
                to="#"
                className="d-inline-flex me-2"
                onClick={() => setShowCc(!showCc)}
              >
                Cc
              </Link>
            </div>
          </div>

          {(cc.length > 0 || showCc) && (
            <div className="position-relative border-bottom chip-with-image">
              <div className="tag-with-img d-flex align-items-center">
                <label className="form-label mb-0">Cc</label>
                <Chips
                  value={cc}
                  className="input-tags form-control border-0 h-100 w-100"
                  onChange={(e: ChipsChangeEvent) => setCc(e.value || [])}
                  onKeyDown={(e) => handleKeyDown(e, cc, setCc)}
                  onBlur={(e) => handleBlur(e, cc, setCc)}
                  itemTemplate={customChip}
                  placeholder="Enter Cc email addresses"
                />
              </div>
            </div>
          )}

          <div className="p-3 border-bottom">
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            {/* <div className="mb-0"> */}
            {/* <textarea
                rows={7}
                className="form-control"
                placeholder="Compose Email"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              /> */}
            {/* <ReactQuill
                theme="snow"
                value={message}
                onChange={setMessage}
                placeholder="Compose your message..."
                modules={quillModules}
                formats={quillFormats}
                style={{ height: "300px", marginBottom: "100px" }}
              /> */}

            <div className=" mb-0 quill-wrapper">
              <ReactQuill
                theme="snow"
                value={message}
                onChange={(value) => setMessage(value)}
                modules={quillModules}
                formats={quillFormats}
              />
            </div>
            {/* </div> */}
          </div>

          {existingAttachments.length > 0 && (
            <div className="p-3 border-bottom">
              <h6>Existing Attachments</h6>
              <div className="d-flex flex-wrap gap-2">
                {existingAttachments.map((file, index) => (
                  <div
                    key={`existing-${index}`}
                    className="d-flex align-items-center bg-light p-2 rounded"
                  >
                    <span className="me-2">{file.filename}</span>
                    <span className="text-muted small me-2">
                      ({Math.round(file.size / 1024)} KB)
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => removeExistingAttachment(index)}
                    >
                      <i className="ti ti-x" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className="p-3 border-bottom">
              <h6>New Attachments</h6>
              <div className="d-flex flex-wrap gap-2">
                {attachments.map((file, index) => (
                  <div
                    key={`new-${index}`}
                    className="d-flex align-items-center bg-light p-2 rounded"
                  >
                    <span className="me-2">{file.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => removeAttachment(index)}
                    >
                      <i className="ti ti-x" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-3 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <button
                type="submit"
                className="btn btn-primary d-inline-flex align-items-center"
                disabled={isLoading || isSavingDraft}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Sending...
                  </>
                ) : (
                  <>Send</>
                )}
              </button>

              <button
                type="button"
                className="btn btn-icon btn-sm rounded-circle me-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <i
                  className="ti ti-paperclip"
                  style={{ fontSize: "20px", marginLeft: "10px" }}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  className="d-none"
                />
              </button>
            </div>
            <div className="d-flex align-items-center compose-footer">
              <button
                type="button"
                className="btn btn-icon btn-sm rounded-circle me-2"
                onClick={handleCloseClick}
                disabled={isLoading || isSavingDraft}
              >
                <i
                  className="ti ti-trash"
                  style={{ fontSize: "20px", marginLeft: "10px" }}
                />
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComposeEmail;
